import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import { publicApi } from './public-client';

/**
 * YOUR OWN ACCOUNT.
 *
 * Every call here is self-scoped by construction: the server reads the caller's
 * id off the session and never takes a target id from the body, so there is no
 * "which user" parameter to get wrong. That is also why these are not in
 * users-client.ts — that one administers OTHER people and is gated on
 * `user:edit`, while none of this is a staff capability at all. A resident
 * corrects their own phone number through exactly these routes.
 */

export interface Profile {
  id: string;
  user_image: string | null;
  full_name: string;
  /** Split server-side: the column is ONE ciphertext of "First Last". */
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string | null;
  email_verified: boolean;
  /**
   * An address that has been requested but not yet proven. The account still
   * signs in with `email` until the link mailed to this one is used, which is
   * the whole point of the pending column — see migration 073.
   */
  pending_email: string | null;
  pending_email_expires_at: string | null;
}

export const getProfile = async (): Promise<Profile> => {
  const { data } = await axios.get(`${apiRoute}/profile`);

  return data as Profile;
};

export interface ProfileUpdate {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  /**
   * A data URI, or null to remove the picture. `undefined` means "not part of
   * this request" — the server keeps the three states distinct, so a name-only
   * save must not send this key at all rather than sending null.
   */
  user_image?: string | null;
}

export const updateProfile = async (patch: ProfileUpdate): Promise<Profile> => {
  const { data } = await axios.put(`${apiRoute}/profile`, patch);

  return data as Profile;
};

/**
 * Ask to move the account to a different address.
 *
 * Returns 202, not 200: nothing has changed yet. The response carries the
 * profile with `pending_email` set, and the address only becomes the login
 * identifier when the link mailed to it is opened.
 */
export const requestEmailChange = async (
  email: string,
  currentPassword: string
): Promise<Profile> => {
  const { data } = await axios.post(`${apiRoute}/profile/email`, {
    email,
    current_password: currentPassword,
  });

  return data as Profile;
};

export const cancelEmailChange = async (): Promise<Profile> => {
  const { data } = await axios.delete(`${apiRoute}/profile/email`);

  return data as Profile;
};

/**
 * Redeem the token from the new mailbox.
 *
 * publicApi, not the authenticated instance: this link is opened wherever the
 * new address is read, which is frequently a phone that has never signed in to
 * Lobbero. Requiring a session would make the common case impossible.
 */
export const confirmEmailChange = async (
  token: string
): Promise<{ changed: boolean; email: string }> => {
  const { data } = await publicApi.post('/email-change/confirm', { token });

  return data as { changed: boolean; email: string };
};

/**
 * Downscale a chosen file into a small square data URI, in the browser.
 *
 * THE RESIZE IS NOT AN OPTIMISATION, it is what makes the feature legal at all:
 * the avatar is stored in `users.user_image` (see the note in the backend's
 * profile.js — the S3 path has no credentials and has never worked), and that
 * column is read on every session probe. A 4 MB phone photo in there would be
 * carried on every /auth. 96px of JPEG at 0.82 lands around 6-10 KB, which is
 * the size a table cell actually renders anyway.
 *
 * Rejects rather than silently returning the original on a decode failure: a
 * "picture saved" toast over an unchanged avatar is worse than an error.
 */
export const AVATAR_PX = 96;

export const fileToAvatarDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!/^image\/(png|jpeg|jpg|webp)$/.test(file.type)) {
      reject(new Error('Choose a PNG, JPEG or WebP image.'));

      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = AVATAR_PX;
      canvas.height = AVATAR_PX;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not process that image.'));

        return;
      }

      // COVER, not stretch: crop to the centre square first. Squashing a
      // portrait into a square avatar is the one transformation that makes
      // everybody look wrong and that nobody can correct afterwards.
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2;
      const sy = (img.height - side) / 2;
      ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_PX, AVATAR_PX);

      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };

    img.src = url;
  });
